import { type ReactNode } from "react";

import { HeartIcon, ShieldCheckIcon } from "lucide-react";

import { compactFormat, numFormat } from "@/shared.utils/number-format";
import { AspectRatio } from "@/shared-web.ui/ui.aspect-ratio";
import { Badge as BaseBadge } from "@/shared-web.ui/ui.badge";
import { Box } from "@/shared-web.ui/ui.box";
import { Flex } from "@/shared-web.ui/ui.flex";
import { Image as BaseImage } from "@/shared-web.ui/ui.image";
import { HardLink } from "@/shared-web.ui/ui.link";
import { Separator } from "@/shared-web.ui/ui.separator";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";
import {
  type BrandDetail as BrandDetailSchema,
  type Decimal,
  type ProductAttributeItem,
  type StockStatus,
} from "@/stf.data/schema";

const BrandDetail = (props: BrandDetailSchema) => {
  const { brandName, brandAuth } = props;
  return (
    <Flex gap="1" align="baseline">
      <Text
        color="gray"
        size="1"
      >
        {brandName}
      </Text>
      {
        ["Authorized Seller", "Brand Owner"].includes(brandAuth)
        && (
          <div className="text-success-10">
            <ShieldCheckIcon
              size={12}
            />
          </div>
        )
      }
    </Flex>
  );
};

interface BadgeProps {
  children: React.ReactNode;
}
const Badge = (props: BadgeProps) => {
  const { children } = props;
  return (
    <BaseBadge>
      { children }
    </BaseBadge>
  );
};

interface BadgeSectionProps {
  children: React.ReactNode;
}
const BadgeSection = (props: BadgeSectionProps) => {
  const { children } = props;

  return (
    <Flex
      direction="row"
      gap="1"
      mx="-1"
      pb="2"
      wrap="wrap"
    >
      {children}
    </Flex>
  );
};

interface ProductNameProps {
  name: string;
}
const ProductName = (props: ProductNameProps) => {
  const { name } = props;
  return (
    <Text
      className="font-serif"
      leading="2"
      lineClamp="2"
      mb="2"
      size={{
        initial: "2",
        sm: "3",
      }}
      weight="medium"
    >
      {name}
    </Text>
  );
};

interface UnavailableContainerProps {
  children: ReactNode;
}
const UnavailableContainer = (props: UnavailableContainerProps) => {
  const { children } = props;
  return (
    <div className="relative">
      {children}
    </div>
  );
};

interface UnavailableOverlayProps {
  stockStatus: StockStatus | null;
}
const UnavailableOverlay = (props: UnavailableOverlayProps) => {
  const { stockStatus } = props;

  if (["Out of Stock", "Sold Out"].includes(stockStatus || "")) {
    return (
      <div className={`
        absolute inset-0 z-10 bg-gray-a6 flex items-center
        rounded-4 justify-center
      `}
      >
        <div className="p-3 bg-gray-3 rounded-2">
          <Text trim="both" color="gray">{stockStatus}</Text>
        </div>
      </div>
    );
  }

  return null;
};

interface ImageProps {
  alt: string;
  src: string;
  stockStatus: StockStatus | null;
}
const ImageVertical = (props: ImageProps) => {
  const { alt, src, stockStatus } = props;
  return (
    <UnavailableContainer>
      <UnavailableOverlay stockStatus={stockStatus} />
      <AspectRatio ratio={1}>
        <BaseImage
          alt={alt}
          className="bg-white-a12"
          responsive={false}
          transformation={[
            {
              width: 500,
              height: 500,
            },
          ]}
          object="cover"
          rounded="4"
          shadow="3"
          src={src}
        />
      </AspectRatio>
    </UnavailableContainer>
  );
};
const ImageHorizontal = (props: ImageProps) => {
  const { alt, src, stockStatus } = props;
  return (
    <UnavailableContainer>
      <UnavailableOverlay stockStatus={stockStatus} />
      <AspectRatio
        ratio={1}
        className={`
          flex justify-center overflow-hidden align-middle
        `}
      >
        <BaseImage
          alt={alt}
          className="bg-white-a12"
          fill={false}
          transformation={[{
            width: 220, height: 220,
          }]}
          height="220"
          object="cover"
          rounded="4"
          shadow="3"
          src={src}
          width="220"
        />
      </AspectRatio>
    </UnavailableContainer>
  );
};

interface PriceProps {
  finalPrice: number;
  stickerPrice: number;
}
const Price = (props: PriceProps) => {
  const { finalPrice, stickerPrice } = props;
  const isDiscount = finalPrice < stickerPrice;

  return (
    <Flex direction="column">
      {isDiscount && (
        <Text
          as="p"
          className={`
            text-[0.7rem] line-through decoration-gray-7
          `}
          color="gray"
          trim="end"
        >
          <Text as="span" className="text-[0.6rem]">
            ฿
          </Text>
          <Text as="span">
            {numFormat(stickerPrice, { decimal: 0 })}
          </Text>
        </Text>
      )}
      <Flex direction="row">
        <Text
          as="p"
          semanticColor={isDiscount ? "danger" : undefined}
        >
          <Text
            as="span"
            size="1"
            weight="light"
          >
            ฿
          </Text>
          <Text
            as="span"
            size="3"
            weight="medium"
          >
            {numFormat(finalPrice, { decimal: 0 })}
          </Text>
        </Text>
      </Flex>
    </Flex>
  );
};

interface GenericAttributeProps {
  attrList: ProductAttributeItem[];
}
const GenericAttribute = (props: GenericAttributeProps) => {
  const { attrList } = props;

  return (
    <ul className={`
      list-inside list-disc px-2 pb-2 text-1 text-gray-11
    `}
    >
      {attrList.slice(0, 5).map((attr) => {
        const { label, value, meta } = attr;

        const attrStr = Array.isArray(value) ? value.join(", ") : value;

        if (!attrStr || meta.hideInDetailed) {
          return null;
        }

        const key = `${label}-${attrStr}`;

        return (
          <li
            className="truncate"
            key={key}
          >
            {attrStr}
          </li>
        );
      })}
    </ul>
  );
};

interface BodyProps {
  children: React.ReactNode;
}
const Body = (props: BodyProps) => {
  const { children } = props;
  return (
    <Flex
      direction="column"
      p={{
        initial: "2",
        sm: "3",
      }}
      pt={{
        initial: "0",
        md: "3",
      }}
      flexGrow="1"
      className="overflow-hidden"
    >
      {children}
    </Flex>
  );
};

interface FooterProps {
  children: React.ReactNode;
}
const Footer = (props: FooterProps) => {
  const { children } = props;
  return (
    <Flex align="end" direction="row" flexGrow="2" justify="between">
      {children}
    </Flex>
  );
};

interface SatisfactionProps {
  ratingDetail: {
    avgRating: Decimal;
    ratingCount: number;
  };
  unitSold: number;
}
const Satisfaction = (props: SatisfactionProps) => {
  const { ratingDetail, unitSold } = props;
  if (!unitSold) {
    return null;
  }

  return (
    <Text
      as="div"
      className="flex flex-row items-baseline gap-1"
      color="gray"
      size="1"
    >
      {
        ratingDetail.avgRating
          ? (
              <>
                <HeartIcon
                  className="size-[0.7em] text-danger-9"
                />
                <Text>
                  {ratingDetail.avgRating}
                </Text>
                <Separator
                  className="h-[0.7em]"
                  orientation="vertical"
                />
              </>
            )
          : null
      }
      <Text>
        {`${compactFormat(unitSold)}`}
      </Text>
      <Box
        className={cn(
          // if there are no rating,
          // showing sold number without "Sold" text
          // will be too confusing
          ratingDetail.avgRating ? "hidden" : "block",
          "sm:block",
        )}
      >
        <Text as="span">Sold</Text>
      </Box>
    </Text>
  );
};

interface PriceRangeProps {
  from: number;
  to: number;
}
const PriceRange = (props: PriceRangeProps) => {
  const { from, to } = props;
  return (
    <Text
      as="div"
      color="gray"
      size="1"
    >
      <Text
        as="span"
        weight="light"
        className="text-[0.5rem]"
      >
        ฿
      </Text>
      {numFormat(from, { decimal: 0 })}
      -
      {numFormat(to, { decimal: 0 })}
    </Text>
  );
};

interface PriceContainerProps {
  children: ReactNode;
}
const PriceContainer = (props: PriceContainerProps) => {
  const { children } = props;

  return (
    <Box
      className="flex flex-col"
    >
      {children}
    </Box>
  );
};

interface CardProps {
  children: React.ReactNode;
  className?: string;
  direction: "column" | "row";
  slugProduct: string;
  slugShop: string;
}
const Card = (props: CardProps) => {
  const { children, className, direction, slugProduct, slugShop } = props;
  return (
    <HardLink
      href={`/shop/${slugShop}/${slugProduct}`}
    >
      <button
        className={cn(`
          w-full rounded-4 bg-panel-solid text-left shadow-3
        `, className)}
        type="button"
      >
        <Flex
          height="100%"
          direction={direction}
          gap="3"
        >
          {children}
        </Flex>
      </button>
    </HardLink>
  );
};

export {
  Badge,
  BadgeSection,
  Body,
  BrandDetail,
  Card,
  Footer,
  GenericAttribute,
  ImageHorizontal,
  ImageVertical,
  Price,
  PriceContainer,
  PriceRange,
  ProductName,
  Satisfaction,
};
